import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"
import { Modal } from "bootstrap"
import { base64urlToBuffer, bufferToBase64url, getCsrfToken } from "../utils/webauthn"

// Fallbacks for the server-side copy passed in through the `translations`
// value; a key the view did not send never reads as `undefined`.
const DEFAULT_TRANSLATIONS = {
  successMessage: ""
}

// Connects to data-controller="passkey-enrollment"
//
// Works alongside the `bs-modal` controller (which auto-opens the modal on connect).
// On connect, this controller feature-detects platform authenticator support and
// silently dismisses the modal when the browser cannot enrol a passkey.
export default class extends Controller {
  static targets = ["enrollButton", "error", "fallback"]
  static values = {
    translations: { type: Object, default: {} },
    challengeUrl: String,
    createUrl: String,
    dismissUrl: String
  }

  // Browser-scoped cache so we don't re-render and then silently dismiss on
  // every page view for users on browsers without a platform authenticator.
  // Self-recovers if the user later enables biometrics or switches browsers.
  static UNSUPPORTED_STORAGE_KEY = "lucanto.passkeyUnsupported"

  get #texts() {
    return { ...DEFAULT_TRANSLATIONS, ...this.translationsValue }
  }

  async connect() {
    if (this.#cachedUnsupported()) {
      this.element.remove()
      return
    }
    if (!window.PublicKeyCredential) {
      this.#markUnsupportedAndDismiss()
      return
    }
    try {
      const available = await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable()
      if (!available) {
        this.#markUnsupportedAndDismiss()
      }
    } catch {
      this.#markUnsupportedAndDismiss()
    }
  }

  #cachedUnsupported() {
    try {
      return localStorage.getItem(this.constructor.UNSUPPORTED_STORAGE_KEY) === "1"
    } catch {
      return false
    }
  }

  #markUnsupportedAndDismiss() {
    try {
      localStorage.setItem(this.constructor.UNSUPPORTED_STORAGE_KEY, "1")
    } catch {
      // Private mode or storage quota: fall through to silent dismiss anyway.
    }
    this.silentDismiss()
  }

  async enroll(event) {
    event.preventDefault()
    this.clearError()
    if (this.hasEnrollButtonTarget) this.enrollButtonTarget.disabled = true

    try {
      const challengeResponse = await this.postJson(this.challengeUrlValue, {})
      if (!challengeResponse.ok) {
        this.showError(await this.extractError(challengeResponse))
        return
      }
      const options = await challengeResponse.json()
      const publicKey = this.decodeCreationOptions(options)

      const credential = await navigator.credentials.create({ publicKey })
      const encoded = this.encodeCredential(credential)

      const createResponse = await this.postJson(this.createUrlValue, { credential: encoded })
      if (!createResponse.ok) {
        this.showError(await this.extractError(createResponse))
        return
      }

      await createResponse.json().catch(() => ({}))
      this.closeModal()
      // Reload the current page rather than following the backend's redirect_to
      // (which points to /settings/profile — relevant only for the settings flow,
      // not this post-task modal). Reload picks up the server-set flash toast.
      Turbo.visit(window.location.href, { action: "replace" })
    } catch (error) {
      if (error.name === "NotAllowedError") {
        // User cancelled the OS prompt — let them retry without a visible error
        this.clearError()
      } else {
        this.showError(error.message)
      }
    } finally {
      if (this.hasEnrollButtonTarget) this.enrollButtonTarget.disabled = false
    }
  }

  async dismiss(_event) {
    // Fired by Remind-later + (X). We post to the backend to increment the
    // dismissal counter and ALSO drive the visual close ourselves. Relying
    // on Bootstrap's `data-bs-dismiss="modal"` alone is unsafe: if the click
    // races with the show-transition, Bootstrap's hide() short-circuits on
    // `_isTransitioning` and the modal stays visible.
    try {
      await this.postJson(this.dismissUrlValue, {})
    } catch {
      // Best-effort: if the dismiss POST fails, the user still sees the modal close.
    }
    this.closeModal()
  }

  silentDismiss() {
    if (this.hasFallbackTarget) this.fallbackTarget.classList.remove("d-none")
    if (this.hasEnrollButtonTarget) this.enrollButtonTarget.disabled = true

    const modalInstance = Modal.getInstance(this.element)
    if (modalInstance) {
      modalInstance.hide()
    } else {
      this.element.classList.remove("show")
      this.element.classList.add("d-none")
    }
  }

  // --- Request helpers ---

  postJson(url, body) {
    return fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        "X-CSRF-Token": getCsrfToken() || ""
      },
      credentials: "same-origin",
      body: JSON.stringify(body)
    })
  }

  async extractError(response) {
    try {
      const data = await response.json()
      return data.error || null
    } catch {
      return null
    }
  }

  // --- UI helpers ---

  showError(message) {
    if (!this.hasErrorTarget) return
    if (!message) {
      this.errorTarget.classList.add("d-none")
      this.errorTarget.textContent = ""
      return
    }
    this.errorTarget.textContent = message
    this.errorTarget.classList.remove("d-none")
  }

  clearError() {
    this.showError(null)
  }

  closeModal() {
    const modalInstance = Modal.getInstance(this.element)
    if (!modalInstance) return
    if (modalInstance._isTransitioning) {
      // Bootstrap's hide() no-ops while the show-transition is still running.
      // Defer until shown.bs.modal so we don't silently leave the modal open.
      this.element.addEventListener("shown.bs.modal", () => modalInstance.hide(), { once: true })
    } else {
      modalInstance.hide()
    }
  }

  // --- WebAuthn encoding ---

  decodeCreationOptions(options) {
    const decoded = {
      ...options,
      challenge: base64urlToBuffer(options.challenge),
      user: { ...options.user, id: base64urlToBuffer(options.user.id) }
    }
    if (options.excludeCredentials) {
      decoded.excludeCredentials = options.excludeCredentials.map((cred) => ({
        ...cred,
        id: base64urlToBuffer(cred.id)
      }))
    }
    return decoded
  }

  encodeCredential(credential) {
    const response = credential.response
    return {
      id: credential.id,
      rawId: bufferToBase64url(credential.rawId),
      type: credential.type,
      response: {
        attestationObject: bufferToBase64url(response.attestationObject),
        clientDataJSON: bufferToBase64url(response.clientDataJSON),
        transports: response.getTransports?.() || []
      }
    }
  }
}
